<template>
  <div class="container">
    <div v-if="loading" class="text-center">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <div v-else-if="!valid" class="text-center" style="max-width: 360px">
      <h1 class="text-center">Link Unavailable</h1>
      <p class="mt-2">{{ errorMessage }}</p>
    </div>

    <div v-else-if="step === 'submitted'" class="text-center" style="max-width: 360px">
      <h1 class="text-center">Thank You</h1>
      <p class="mt-2">Your document has been received.</p>
    </div>

    <!-- STEP 1: pick files -->
    <div v-else-if="step === 'picking'" class="text-center" style="max-width: 360px; width: 100%">
      <h1 class="text-center">Upload a Document</h1>
      <p class="mt-2">
        {{ contactFirstName ? `Hi ${contactFirstName}, please` : "Please" }} upload a document
        for {{ clientName || "your provider" }}.
      </p>

      <v-file-input v-model="files" multiple label="Choose file(s)" class="mt-4" />
      <v-text-field v-model="caption" label="Note (optional)" class="mt-2" />

      <v-btn
        color="primary"
        width="300"
        height="50"
        class="mt-4 text-capitalize font-weight-bold"
        :loading="analyzing"
        :disabled="!files || files.length === 0"
        @click="analyze"
      >
        Upload
      </v-btn>

      <p v-if="analyzeError" class="mt-2" style="color: red">{{ analyzeError }}</p>
    </div>

    <!-- STEP 2: review/correct extracted fields before it goes to staff -->
    <div v-else-if="step === 'reviewing'" class="text-left" style="max-width: 900px; width: 100%">
      <h1 class="text-center">Review Your Document</h1>
      <p class="mt-2 text-center">
        We read the following from your document — please fix anything that looks wrong before sending.
      </p>

      <v-card v-for="(file, index) in reviewFiles" :key="index" class="mt-4 pa-3">
        <v-chip v-if="file.classified_type" size="small" label class="mb-2">
          {{ file.classified_type }}
        </v-chip>

        <v-alert v-if="file.low_confidence" type="warning" density="compact" class="mb-3">
          We had trouble reading this document clearly. Please double-check the fields below,
          or upload a clearer photo if possible.
        </v-alert>

        <div class="review-row">
          <div class="image-panel">
            <ZoomableImage :src="file.imageUrl" height="320px" />
          </div>

          <!-- Extracted fields -->
          <div class="fields-panel">
            <div v-if="file.extracted_data">
              <v-text-field
                v-for="field in file.field_definitions"
                :key="field.key"
                density="compact"
                :label="field.label"
                v-model="file.extracted_data[field.key]"
              />
            </div>
            <p v-else class="text-grey">No details extracted from this file.</p>
          </div>
        </div>
      </v-card>

      <div class="text-center">
        <v-btn
          color="primary"
          width="300"
          height="50"
          class="mt-4 text-capitalize font-weight-bold"
          :loading="confirming"
          @click="confirm"
        >
          Confirm & Submit
        </v-btn>

        <p v-if="confirmError" class="mt-2" style="color: red">{{ confirmError }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { toRaw } from "vue";
import MediaUploadLinkService from "@/services/mediaUploadLinkService";
import ZoomableImage from "@/components/UIComponents/ZoomableImage.vue";

export default {
  name: "SecureUpload",
  components: { ZoomableImage },
  data() {
    return {
      loading: true,
      valid: false,
      errorMessage: "",
      contactFirstName: null,
      clientName: null,
      step: "picking", // picking -> reviewing -> submitted
      files: [],
      caption: "",
      analyzing: false,
      analyzeError: "",
      reviewFiles: [],
      confirming: false,
      confirmError: "",
    };
  },
  mounted() {
    this.validateLink();
  },
  beforeUnmount() {
    this.revokeImageUrls();
  },
  methods: {
    async validateLink() {
      this.loading = true;
      try {
        const res = await MediaUploadLinkService.validate(this.$route.params.token);
        this.valid = true;
        this.contactFirstName = res.data.contact_first_name;
        this.clientName = res.data.client_name;
      } catch (err) {
        this.valid = false;
        this.errorMessage =
          err.response?.data?.message || "This link is invalid or has expired.";
      } finally {
        this.loading = false;
      }
    },
    async analyze() {
      this.analyzing = true;
      this.analyzeError = "";
      // Snapshot the picked files synchronously, before the `await` below —
      // this guarantees the array used for local previews is exactly what
      // was uploaded, regardless of whether `this.files` (the v-file-input
      // model) changes or gets reset while the request is in flight.
      // toRaw() unwraps each File from Vue's reactivity Proxy (added when
      // v-file-input's array lands in this component's reactive data) —
      // native browser APIs like URL.createObjectURL do strict internal
      // type-checks that a proxied File fails, throwing "parameter 1 is not
      // of type 'Blob'".
      const uploadedFiles = this.files.map((f) => toRaw(f));
      try {
        const res = await MediaUploadLinkService.analyze(this.$route.params.token, uploadedFiles);
        // Local object URLs so the review screen can show the actual image
        // the patient picked, without needing a new public "read pending
        // upload" backend endpoint — the browser already has the bytes.
        // Wrapped per-file so one bad File object can't take down the whole
        // review step — worst case that file just renders without a preview.
        const files = res.data.files.map((f, i) => {
          let imageUrl = null;
          try {
            imageUrl = uploadedFiles[i] ? URL.createObjectURL(uploadedFiles[i]) : null;
          } catch (err) {
            console.error("Failed to create image preview:", err);
          }
          return { ...f, imageUrl };
        });

        // Nothing to review (AI disabled for this practice) — submit straight away.
        if (!res.data.ai_enabled) {
          this.reviewFiles = files;
          await this.confirm();
          return;
        }
        this.reviewFiles = files;
        this.step = "reviewing";
      } catch (err) {
        this.analyzeError = err.response?.data?.message || "Something went wrong. Please try again.";
      } finally {
        this.analyzing = false;
      }
    },
    async confirm() {
      this.confirming = true;
      this.confirmError = "";
      try {
        const payload = this.reviewFiles.map((f) => ({ extracted_data: f.extracted_data }));
        await MediaUploadLinkService.confirm(this.$route.params.token, payload, this.caption);
        this.revokeImageUrls();
        this.step = "submitted";
      } catch (err) {
        this.confirmError = err.response?.data?.message || "Something went wrong. Please try again.";
      } finally {
        this.confirming = false;
      }
    },
    revokeImageUrls() {
      this.reviewFiles.forEach((f) => {
        if (f.imageUrl) URL.revokeObjectURL(f.imageUrl);
      });
    },
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100%;
  width: 100%;
  padding: 24px;
  text-align: center;
}

.review-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.image-panel {
  flex: 1 1 320px;
  max-width: 380px;
}

.fields-panel {
  flex: 1 1 320px;
}
</style>
